// Copyright (c) Docugami, Inc. All rights reserved.
import axios from "axios";
import { Config } from "config";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DownloadFileClickedSchema, DownloadFilesClickedSchema, TelemetryEvent } from 'telemetry/TelemetryEvents';
import { Telemetry } from 'telemetry/TelemetryManager';
import { getLocalStorageLongDownloadsInfo, setLocalStorageLongDownloadsInfo } from "utils/localStorageHelpers";
import { Logger, LoggerCategory } from "utils/Logger";
const { v4: uuidv4 } = require('uuid');

interface FilesDownloadInfo {
    cookie: string;
    organizationId: string;
    initiatedTime?: number;
}

export interface CachedDownloadStatus {
    curFilesDownloadInfo?: FilesDownloadInfo;
}

// constant for an hour
const DownloadMaxWindow = 60 * 60 * 1000;

export class DgDownloader {
    public static instance: DgDownloader = new DgDownloader();
    private listeners: { [cookie: string]: () => void } = {};
    private curFilesDownloadInfo?: FilesDownloadInfo;

    private constructor() {
        const cachedStatus = getLocalStorageLongDownloadsInfo();
        if (!!cachedStatus) {
            if (!!cachedStatus.curFilesDownloadInfo?.cookie) {
                this.curFilesDownloadInfo = cachedStatus.curFilesDownloadInfo;
                this.pollFilesDownload(this.curFilesDownloadInfo);
            }

            setLocalStorageLongDownloadsInfo({
                curFilesDownloadInfo: this.curFilesDownloadInfo
            })
        }
    }

    public addListener(cb: () => void): string {
        let cookie = uuidv4();
        this.listeners[cookie] = cb;
        return cookie;
    }

    public removeListener(cookie: string): void {
        delete this.listeners[cookie];
    }

    public canDownloadFiles(): boolean {
        return !this.curFilesDownloadInfo;
    }

    // download single file
    public async downloadFile(fileId: string, downloadFileName: string, organizationId: string) {
        Telemetry.trackEvent<DownloadFileClickedSchema>({
            eventName: TelemetryEvent.DownloadFileClicked
        });

        if (!!this.curFilesDownloadInfo) {
            throw new Error("only one files download is supported at a time");
        }

        this.setFilesDownloadInfo(organizationId, '');

        const response = await axios.post(
            `${Config.fileManagerApiUrl}/FileManager/DownloadFile`,
            fileId, {
            responseType: "blob",
        });
        const blob = response.data;
        const a = document.createElement('a');

        a.href = URL.createObjectURL(blob);
        a.download = downloadFileName;
        a.click();
        URL.revokeObjectURL(blob);
        this.filesDownloadComplete();
    }

    // download document split zip for a file
    public async downloadFileSplits(fileId: string, downloadFileName: string, organizationId: string) {
        if (!!this.curFilesDownloadInfo) {
            throw new Error("only one file download is supported at a time");
        }

        this.setFilesDownloadInfo(organizationId, '');

        const response = await axios.get(
            `${Config.apiUrl}/documents/${fileId}/split`,
            {
            responseType: "blob",
            });
        const blob = response.data;
        const a = document.createElement('a');

        a.href = URL.createObjectURL(blob);
        a.download = downloadFileName;
        a.click();
        URL.revokeObjectURL(blob);
        this.filesDownloadComplete();
    }

    // download multiple files/folders as a zip file
    public async downloadFiles(files: string[], organizationId: string) {
        Telemetry.trackEvent<DownloadFilesClickedSchema>({
            eventName: TelemetryEvent.DownloadFilesClicked
        });

        if (!!this.curFilesDownloadInfo) {
            throw new Error("only one files/dgmlx download is supported at a time");
        }

        this.setFilesDownloadInfo(organizationId, '');
        try {
            const response = await axios.post(
                `${Config.fileManagerApiUrl}/FileManager/DownloadFilesStart`,
                files);
            const cookie = response.data;
            this.setFilesDownloadInfo(organizationId, cookie);
            this.pollFilesDownload(this.curFilesDownloadInfo!)
        } catch (error) {
            Logger.LogError(LoggerCategory.Downloader, `Failed start files download for org ${organizationId}`);
            this.filesDownloadComplete();
        }
    }

    // download dgxml a zip file
    public async downloadDgmlx(fileId: string, organizationId: string) {
        if (!!this.curFilesDownloadInfo) {
            throw new Error("only one files/dgmlx download is supported at a time");
        }

        this.setFilesDownloadInfo(organizationId, '');
        try {
            const response = await axios.post(
                `${Config.fileManagerApiUrl}/FileManager/DownloadDgmlx`,
                JSON.stringify(fileId),
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
            const cookie = response.data;
            this.setFilesDownloadInfo(organizationId, cookie);
            this.pollFilesDownload(this.curFilesDownloadInfo!)
        } catch (error) {
            Logger.LogError(LoggerCategory.Downloader, `Failed start dgmlx download for fileId ${fileId} org ${organizationId}`);
            this.filesDownloadComplete();
        }
    }

    private pollFilesDownload(downloadInfo: FilesDownloadInfo) {
        if (!downloadInfo) {
            this.filesDownloadComplete();
            return;
        }

        this.pollDownloadStatus(
            `${Config.fileManagerApiUrl}/FileManager/downloadfiles/status/${downloadInfo.cookie}`,
            downloadInfo.initiatedTime,
            () => this.downloadProcessedZipFile(),
            () => this.filesDownloadComplete());
    }

    // polls the download files status
    private pollDownloadStatus(statusUrl: string, initiatedTime: number | undefined, successCallback: () => void, failureCallback: () => void) {
        const statusIntervalInMilliseconds = 3000;

        function pollAndDownload(dgDownloader: DgDownloader) {

            axios.get(statusUrl)
                .then(response => {
                    console.log(response.data.customStatus);
                    if (response.data.customStatus === "ReadyForDownload") {
                        successCallback();
                        return;
                    }
                    else if (response.data.customStatus === "Expired" ||
                        response.data.runtimeStatus === 3 /*failed*/ ||
                        response.data.runtimeStatus === 5 /*terminated*/) {
                        failureCallback();
                        return;
                    }

                    if (!!initiatedTime && (initiatedTime - Date.now()) > DownloadMaxWindow) {
                        failureCallback();
                    }

                    window.setTimeout(pollAndDownload, statusIntervalInMilliseconds, dgDownloader);
                })
        }

        window.setTimeout(pollAndDownload, statusIntervalInMilliseconds, this);
    }

    private async downloadProcessedZipFile() {
        const response = await axios.get(
            `${Config.fileManagerApiUrl}/FileManager/DownloadZipFile?fileid=${this.curFilesDownloadInfo?.cookie}`, {
            responseType: "blob"
        });

        const a = document.createElement('a');
        const blob = new Blob([response.data], { 'type': "application/zip" });
        a.href = URL.createObjectURL(blob);
        a.download = this.curFilesDownloadInfo?.cookie + ".zip";
        a.click();
        URL.revokeObjectURL(a.href);
        this.filesDownloadComplete();
    }

    private filesDownloadComplete() {
        this.curFilesDownloadInfo = undefined;
        this.notifyListeners();
    }

    private setFilesDownloadInfo(organizationId: string, cookie: string) {
        this.curFilesDownloadInfo = {
            organizationId: organizationId,
            cookie: cookie,
            initiatedTime: Date.now()
        };

        this.notifyListeners();
    }

    private notifyListeners() {
        setLocalStorageLongDownloadsInfo({
            curFilesDownloadInfo: this.curFilesDownloadInfo
        })
        for (let cb of Object.values(this.listeners)) {
            cb();
        }
    }
}

export interface IDgDownloader {
    downloadFiles(files: string[]): void;
    downloadDgmlx(fileId: string): void;
    downloadFile(fileId: string, fileName: string): void;
    downloadFileSplits(fileId: string, fileName: string): void;
    canDownloadFiles(): boolean;
}

export function useDgDownloader(organizationId: string): IDgDownloader {
    const [version, setVersion] = useState(0)
    const callback = useCallback(() => { setVersion(v => v + 1); }, []);

    useEffect(() => {
        let cookie = DgDownloader.instance.addListener(callback);

        return () => {
            DgDownloader.instance.removeListener(cookie);
        };
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [])

    return useMemo<IDgDownloader>(() => {
        return {
            downloadFiles: (files: string[]) => DgDownloader.instance.downloadFiles(files, organizationId),
            downloadDgmlx: (fileId: string) => DgDownloader.instance.downloadDgmlx(fileId, organizationId),
            downloadFile: (fileId: string, fileName: string) => DgDownloader.instance.downloadFile(fileId, fileName, organizationId),
            downloadFileSplits: (fileId: string, fileName: string) => DgDownloader.instance.downloadFileSplits(fileId, fileName, organizationId),
            canDownloadFiles: () => DgDownloader.instance.canDownloadFiles()
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [version, organizationId]);
}
